import bs58 from "bs58";
import { GridApiClient } from "../api/grid-api-client";
import { base64ToBytes } from "../utils/encoding";
import { AccountManager } from "../managers/account-manager";
import {
  AuthorizationError,
  GetPasskeysResponse,
  AddPasskeyRequest,
  AddPasskeyQueryParams,
  AddPasskeyResponse,
  RemovePasskeyRequest,
  RemovePasskeyQueryParams,
  RemovePasskeyResponse,
  PasskeyRequest,
  SessionKey,
  RP_NAME,
  CreatePasskeySessionRequest,
  CreatePasskeySessionResponse,
  AuthorizePasskeySessionRequest,
  AuthorizePasskeySessionResponse,
  SubmitPasskeySessionRequest,
  SubmitPasskeySessionResponse,
  FindPasskeyAccountRequest,
  FindPasskeyAccountResponse,
  GetPasskeySessionResponse,
  GetPasskeyAccountResponse,
  CreatePasskeyAccountRequest,
  CreatePasskeyAccountResponse,
  PasskeyCredentialData,
  PasskeyAssertionResponse,
} from "../types";

/**
 * @hidden
 * Service handling passkey operations
 */
export class PasskeyService {
  private apiClient: GridApiClient;
  private accountManager?: AccountManager;

  constructor(apiClient: GridApiClient, accountManager?: AccountManager) {
    this.apiClient = apiClient;
    this.accountManager = accountManager;
  }

  /**
   * Retrieves all passkeys for an account
   *
   * @param accountAddress - The account address to get passkeys for
   * @returns Promise resolving to passkeys response with credential details
   *
   * @example
   * ```typescript
   * const passkeys = await gridClient.getPasskeys(accountAddress);
   * ```
   *
   * @category Passkey Management
   */
  async getPasskeys(accountAddress: string): Promise<GetPasskeysResponse> {
    const response = await this.apiClient.getPasskeys(accountAddress);
    return response;
  }

  /**
   * Registers a new passkey for an account.
   *
   * @param accountAddress - The account address to add the passkey to
   * @param request - Passkey registration request with credential data
   * @param queryParams - Optional query parameters for the registration
   * @returns Promise resolving to passkey addition response
   *
   * @example
   * ```typescript
   * const passkeyResponse = await gridClient.addPasskey(accountAddress, {
   *   passkey: {
   *     address: "2W6cUhpELHYhTi3Cn6SmHfVhJFKgqmUJLJ5V2DQNcaXE",
   *     role: "secondary",
   *     permissions: ["CAN_INITIATE", "CAN_VOTE", "CAN_EXECUTE"]
   *   }
   * });
   * ```
   *
   * @category Passkey Management
   */
  async addPasskey(
    accountAddress: string,
    request: AddPasskeyRequest,
    queryParams?: AddPasskeyQueryParams
  ): Promise<AddPasskeyResponse> {
    const response = await this.apiClient.addPasskey(
      accountAddress,
      request,
      queryParams
    );
    return response;
  }

  /**
   * Removes an existing passkey credential from an account, revoking its
   * ability to authorize transactions.
   *
   * @param accountAddress - The account address that owns the passkey
   * @param passkeyAddress - The address of the passkey to remove
   * @param request - Passkey removal request with authorization details
   * @param queryParams - Optional query parameters for the removal
   * @returns Promise resolving to passkey removal response
   *
   *
   * @example
   * ```typescript
   * const removeResponse = await gridClient.removePasskey(
   *   accountAddress,
   *   passkeyAddress
   * );
   * ```
   *
   * @category Passkey Management
   */
  async removePasskey(
    accountAddress: string,
    passkeyAddress: string,
    transactionSigners?: string[],
    queryParams?: RemovePasskeyQueryParams
  ): Promise<RemovePasskeyResponse> {
    const response = await this.apiClient.removePasskey(
      accountAddress,
      passkeyAddress,
      transactionSigners,
      queryParams
    );
    return response;
  }

  /**
   * Initiates the WebAuthn credential creation process, prompting the user
   * to register a new passkey using their device's authenticator (fingerprint,
   * face recognition, or security key).
   *
   * @param params - Passkey creation parameters including challenge and user info
   * @returns Promise resolving to credential data with public key and credential ID
   *
   *
   * @example
   * ```typescript
   * const credentialData = await gridClient.createPasskeyCredentials({
   *   env: 'production',
   *   challenge: 'base64-encoded-challenge',
   *   slotNumber: '123',
   *   sessionKey: 'session-public-key-base58',
   *   expirationInSeconds: '3600',
   *   appName: 'My App',
   *   userId: 'user-identifier',
   *   redirectUrl: 'your-redirect-url'
   * });
   * ```
   * Note: You can use extractPasskeyCreateParams to extract the parameters from the URL.
   *
   * @category Passkey Management
   */
  async createPasskeyCredentials(params: PasskeyRequest): Promise<PasskeyCredentialData>{
    try {
      const passkeyParams = {
        publicKey: {
          challenge: base64ToBytes(params.challenge).buffer as ArrayBuffer,
          rp: {
            name: RP_NAME,
          },
          user: {
            id: new TextEncoder().encode(params.userId),
            name: `${params.appName}#${params.userId}`,
            displayName: params.appName,
          },
          pubKeyCredParams: [
            { type: "public-key", alg: -7 },   // ES256
            { type: "public-key", alg: -257 }, // RS256
          ],
          authenticatorSelection: {
            residentKey: "required",
            requireResidentKey: true,
            userVerification: "required",
          },
          timeout: 60000,
          attestation: "direct",
        },
      } as CredentialCreationOptions;

      const credential = (await navigator.credentials.create(passkeyParams)) as PublicKeyCredential;
      if (!credential) {
        throw new AuthorizationError(
          "CREDENTIAL_CREATION_FAILED",
          "Could not create passkey credentials"
        );
      }

      const res = credential.response as AuthenticatorAttestationResponse;
      if (!res.getPublicKey()) {
        throw new AuthorizationError(
          "NO_PUBLIC_KEY",
          "No public key found"
        );
      }

      const publicKey = this.bufferToBase64Url(res.getPublicKey()!);

      // Return both the credential ID and public key for proper passkey flow
      return {
        id: credential.rawId,  // Raw ArrayBuffer for use in allowCredentials
        publicKey: publicKey,  // Base64 encoded public key
        credentialId: credential.id  // Base64url encoded ID string
      };
    } catch (error) {
      throw new AuthorizationError(
        `Passkey creation failed: ${error instanceof Error ? error.message : String(error)}`,
        "PASSKEY_CREATION_FAILED"
      );
    }
  }

  /**
   * Initiates the WebAuthn authentication process, prompting the user to
   * authenticate using their previously registered passkey.
   *
   * @param params - Passkey authentication parameters including challenge
   * @param credentialData - Optional specific credential to authenticate with
   * @returns Promise resolving to assertion response with signature and authentication data
   *
   *
   * @example
   * ```typescript
   * const assertion = await gridClient.getPasskeyCredentials({
   *   env: 'production',
   *   challenge: 'base64-encoded-challenge',
   *   slotNumber: '123',
   *   sessionKey: 'session-public-key-base58',
   *   expirationInSeconds: '3600',
   *   appName: 'My App',
   *   userId: 'user-identifier',
   *   redirectUrl: 'your-redirect-url'
   * });
   * console.log(assertion.response.signature);
   * ```
   * Note: You can use extractPasskeyAuthParams to extract the parameters from the URL.
   *
   * @category Passkey Management
   */
  async getPasskeyCredentials(params: PasskeyRequest, credentialData?: PasskeyCredentialData): Promise<PasskeyAssertionResponse> {
    try {
      const assertionParams = {
        publicKey: {
          challenge: base64ToBytes(params.challenge).buffer as ArrayBuffer,
          allowCredentials: credentialData ?  [{
            id: credentialData.id,  // This is the rawId ArrayBuffer from creation
            type: 'public-key'
          }]:[],
          userVerification: "required",
          timeout: 60000,
        },
      } as CredentialRequestOptions;

      const assertion = (await navigator.credentials.get(assertionParams)) as PublicKeyCredential;

      if (!assertion) {
          throw new AuthorizationError(
            "ASSERTION_FAILED",
            "Could not get passkey credentials"
          );
      }

      // Make sure we're converting everything to plain objects
      const response = (assertion as PublicKeyCredential)
        .response as AuthenticatorAssertionResponse;

      const signature = this.bufferToBase64Url(response.signature);
      const clientDataJSON = this.bufferToBase64Url(response.clientDataJSON);
      const authenticatorData = this.bufferToBase64Url(response.authenticatorData);

      if (credentialData) {
        // Convert publicKey to base64 if it's not already
        const publicKeyBase64 = typeof credentialData.publicKey === 'string'
          ? credentialData.publicKey
          : this.bufferToBase64Url(credentialData.publicKey);

        const obj = {
          response: {
            signature,
            clientDataJSON,
            authenticatorData,
            publicKey: publicKeyBase64,
          },
        };

        return obj;
      } else {
        const obj = {
          response: {
            signature,
            clientDataJSON,
            authenticatorData,
          },
        };
        return obj;
      }
    } catch (error) {
      throw new AuthorizationError(
        `Passkey assertion failed: ${error instanceof Error ? error.message : String(error)}`,
        "PASSKEY_ASSERTION_FAILED"
      );
    }
  }

  /**
   * Parses URL search parameters to extract the necessary information
   * for passkey credential creation.
   *
   * @param query - URLSearchParams object containing passkey creation parameters
   * @returns Parsed passkey request object ready for credential creation
   *
   * @example
   * ```typescript
   * const urlParams = new URLSearchParams(window.location.search);
   * const passkeyParams = gridClient.extractPasskeyCreateParams(urlParams);
   *
   * const credentials = await gridClient.createPasskeyCredentials(passkeyParams);
   * ```
   *
   * @category Passkey Management
   */
  extractPasskeyCreateParams(query: URLSearchParams): PasskeyRequest {
    const params = {
      env: query.get("env"),
      appName: query.get("app-name"),
      userId: query.get("user-id"),
      redirectUrl: query.get("redirect-url"),
      challenge: query.get("challenge"),
      slotNumber: query.get("slot-num"),
      sessionKey: query.get("session-key"),
      expirationInSeconds: query.get("expiration-in-seconds"),
    } as PasskeyRequest;

    return params;
  }

  /**
   * Parses URL search parameters to extract the necessary information
   * for passkey authentication.
   *
   * @param query - URLSearchParams object containing passkey authentication parameters
   * @returns Parsed passkey request object ready for authentication
   *
   * @example
   * ```typescript
   * const urlParams = new URLSearchParams(window.location.search);
   * const authParams = gridClient.extractPasskeyAuthParams(urlParams);
   *
   * const assertion = await gridClient.getPasskeyCredentials(authParams);
   * ```
   *
   * @category Passkey Management
   */
  extractPasskeyAuthParams(query: URLSearchParams): PasskeyRequest {
    const params = {
      env: query.get('env'),
      redirectUrl: query.get('redirect-url'),
      challenge: query.get('challenge'),
      slotNumber: query.get('slot-num'),
      sessionKey: query.get('session-key'),
      expirationInSeconds: query.get('expiration-in-seconds'),
    } as PasskeyRequest;

    return params;
  }

  /**
   * Initiates a new passkey authentication session, preparing the necessary
   * challenge and session data for WebAuthn authentication flow.
   *
   * @param params - Passkey session creation parameters
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to passkey session creation response
   *
   *
   * @example
   * ```typescript
   * await gridClient.createPasskeySession({
   *   action: 'create',
   *   sessionKey: 'session-public-key-base58',
   *   env: 'production',
   * }, 'production');
   * ```
   *
   * @category Passkey Sessions
   */
  async createPasskeySession(
    params: CreatePasskeySessionRequest,
    environment: string
  ): Promise<CreatePasskeySessionResponse> {
    try {
      const response = await this.apiClient.createPasskeySession(params, environment);

      return response;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Create passkey session failed: ${error.message}`,
        "PASSKEY_SESSION_CREATE_FAILED"
      );
    }
  }

  /**
   * Submits passkey authentication data to authorize an existing session,
   * completing the WebAuthn authentication flow.
   *
   * @param params - Passkey session authorization parameters with authentication data
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to session authorization response
   *
   *
   * @example
   * ```typescript
   * const authorization = await gridClient.authorizePasskeySession({
   *   sessionKey: 'public-key-base58',
   *   metaInfo: {
   *     appName: 'My App',
   *     redirectUrl: 'https://myapp.com/callback'
   *   },
   *   baseUrl: 'base-url'
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async authorizePasskeySession(
    params: AuthorizePasskeySessionRequest,
    environment: string
  ): Promise<AuthorizePasskeySessionResponse> {
    try {
      const response = await this.apiClient.authorizePasskeySession(params, environment);

      return response;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Authorize passkey session failed: ${error.message}`,
        "PASSKEY_SESSION_AUTHORIZE_FAILED"
      );
    }
  }

  /**
   * Finalizes a passkey authentication session, generating the final
   * session tokens and account access credentials.
   *
   * @param params - Passkey session submission parameters
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to session submission response with account details
   *
   *
   * @example
   * ```typescript
   * const result = await gridClient.submitPasskeySession({
   *   authenticatorResponse: <attestationObject from getPasskeySession>,
   *   sessionKey: 'public-key-base58',
   *   ceremonyType: 'auth',
   *   slotNumber: 123
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async submitPasskeySession(
    params: SubmitPasskeySessionRequest,
    environment: string
  ): Promise<SubmitPasskeySessionResponse> {
    try {
      const response = await this.apiClient.submitPasskeySession(params, environment);

      // Response is already transformed to camelCase by API client
      // Only encode sessionKey.key from byte array to base58
      const sessionKey = bs58.encode(new Uint8Array(response.data.sessionKey.key));

      return {
        data: {
          passkeyAccount: response.data.passkeyAccount,
          smartAccount: response.data.smartAccount,
          sessionKey: {
            key: sessionKey,
            expiration: response.data.sessionKey.expiration
          },
        },
        lastResponse: response.lastResponse
      };
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Submit passkey session failed: ${error.message}`,
        "PASSKEY_SESSION_SUBMIT_FAILED"
      );
    }
  }

  /**
   * Searches for an existing passkey account using credential information,
   * allowing users to recover access to their accounts.
   *
   * @param params - Passkey account search parameters with credential data
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to found passkey account information
   *
   *
   * @example
   * ```typescript
   * const account = await gridClient.findPasskeyAccount({
   *   sessionKey: 'public-key-base58',
   *   authenticatorResponse: <attestationObject from getPasskeySession>
   * }, 'sandbox');
   * ```
   *
   * @category Passkey Sessions
   */
  async findPasskeyAccount(
    params: FindPasskeyAccountRequest,
    environment: string
  ): Promise<FindPasskeyAccountResponse> {
    try {
      const response = await this.apiClient.findPasskeyAccount(params, environment);

      // Response is already transformed to camelCase by API client
      // Convert the response format if it contains the expected backend structure
      if (response.data.passkeyAccount && response.data.sessionKey) {
        // Only encode sessionKey.key from byte array to base58
        const sessionKeyBase58 = bs58.encode(new Uint8Array(response.data.sessionKey.key));
        return {
          data: {
            passkeyAccount: response.data.passkeyAccount,
            address: response.data.address,
            sessionKey: {
                key: sessionKeyBase58,
                expiration: response.data.sessionKey.expiration
              }
            },
            lastResponse: response.lastResponse
          };
      }

      return response;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Find passkey account failed: ${error.message}`,
        "PASSKEY_ACCOUNT_FIND_FAILED"
      );
    }
  }

  /**
   * Retrieves detailed information about a specific passkey account,
   * including relying party ID, public key, and optional session key if not expired.
   *
   * @param passkeyAddress - The passkey account address (Solana public key) to retrieve
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to passkey account details with relying party ID, public key, and optional session key
   *
   *
   * @example
   * ```typescript
   * const account = await gridClient.getPasskeyAccount(
   *   'passkey-account-address',
   *   'sandbox'
   * );
   * console.log(account.data.relyingPartyId);
   * console.log(account.data.pubkey);
   * if (account.data.sessionKey) {
   *   console.log('Session key:', account.data.sessionKey.key);
   *   console.log('Expires:', account.data.sessionKey.expiration);
   * }
   * ```
   *
   * @category Passkey Sessions
   */
  async getPasskeyAccount(
    passkeyAddress: string,
    environment: string
  ): Promise<GetPasskeyAccountResponse> {
    try {
      if (!passkeyAddress) {
        throw new AuthorizationError(
          "Passkey address is required",
          "INVALID_REQUEST"
        );
      }

      const response = await this.apiClient.getPasskeyAccount(passkeyAddress, environment);

      return response;
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Get passkey account failed: ${error.message}`,
        "PASSKEY_ACCOUNT_GET_FAILED"
      );
    }
  }

  /**
   * Creates both a passkey account (externally signed account) and smart account in one call.
   * This combines the passkey creation ceremony with smart account initialization.
   * Creates a 1/1 smart account with the passkey having full permissions.
   *
   * @param params - Passkey account creation parameters including session key and authenticator response
   * @param environment - Target environment ('sandbox' or 'production')
   * @returns Promise resolving to the created smart account details including passkey info
   *
   *
   * @example
   * ```typescript
   * const sessionKeyObject = {
   *   key: sessionKeyPubkey,
   *   expiration: Date.now() + 900000
   * };
   *
   * const account = await gridClient.createPasskeyAccount({
   *   sessionKey: sessionKeyObject,
   *   slotNumber: 123,
   *   authenticatorResponse: webauthnResponse,
   *   adminAddress: 'admin-public-key',
   *   memo: 'My passkey account'
   * }, 'sandbox');
   * console.log('Smart account address:', account.data.address);
   * console.log('Passkey account:', account.data.passkey.passkeyAccount);
   * console.log('Session key:', account.data.passkey.sessionKey);
   * ```
   *
   * @category Passkey Management
   */
  async createPasskeyAccount(
    params: CreatePasskeyAccountRequest,
    environment: string
  ): Promise<CreatePasskeyAccountResponse> {
    try {
      if (!params.sessionKey || !params.slotNumber || !params.authenticatorResponse) {
        throw new AuthorizationError(
          "Session key, slot number, and authenticator response are required",
          "INVALID_REQUEST"
        );
      }
      const response = await this.apiClient.createPasskeyAccount(params, environment);

      // Check for passkey session in authentication array (new API structure)
      // API returns: authentication[].session.Passkey.session_key
      const passkeyAuth = response.data.authentication?.find(
        (auth: any) => auth.provider === 'passkey' 
      );

      if (passkeyAuth?.session?.Passkey || passkeyAuth?.session?.passkey) {
        // Support both capital P (Passkey) and lowercase p (passkey)
        const passkeySession = passkeyAuth.session.Passkey || passkeyAuth.session.passkey;
        // API returns snake_case keys (session_key), but support both formats
        const rawSessionKey = passkeySession.session_key?.key || passkeySession.sessionKey?.key;

        if (!rawSessionKey) {
          throw new AuthorizationError(
            "Invalid response: missing session key in passkey session",
            "INVALID_RESPONSE"
          );
        }

        // Flatten passkey session data to top-level for easier access
        // Transform from: { provider, session: { Passkey: {...} } }
        // To: { provider, sessionKey, passkeyAccount, pubkey, relyingPartyId, ... }
        const updatedAuthentication = response.data.authentication.map((auth: any) => {
          if (auth === passkeyAuth) {
            return {
              provider: auth.provider,
              // Transform snake_case API response to camelCase for SDK
              sessionKey: {
                key: passkeySession.session_key?.key || passkeySession.sessionKey?.key,
                expiration: passkeySession.session_key?.expiration || passkeySession.sessionKey?.expiration
              },
              passkeyAccount: passkeySession.passkey_account || passkeySession.passkeyAccount,
              pubkey: passkeySession.pubkey,
              relyingPartyId: passkeySession.relying_party_id || passkeySession.relyingPartyId,
              // Keep original nested structure for reference
              session: auth.session
            };
          }
          return auth;
        });

        // Return response with flattened authentication structure
        return {
          data: {
            ...response.data,
            authentication: updatedAuthentication
          },
          lastResponse: response.lastResponse
        };
      }

      // No passkey session found (shouldn't happen for create, but handle gracefully)
      throw new AuthorizationError(
        "Invalid response: missing passkey session data",
        "INVALID_RESPONSE"
      );
    } catch (error: any) {
      if (error instanceof AuthorizationError) {
        throw error;
      }
      throw new AuthorizationError(
        `Create passkey account failed: ${error.message}`,
        "PASSKEY_ACCOUNT_CREATE_FAILED"
      );
    }
  }

  /**
   * @hidden
   * Convert ArrayBuffer to base64url encoding
   *
   * @param buffer - ArrayBuffer containing binary data to encode
   * @returns Base64url encoded string
   *
   * @example
   * ```typescript
   * const binaryData = new Uint8Array([1, 2, 3, 4]).buffer;
   * const encoded = gridClient.bufferToBase64Url(binaryData);
   * console.log(encoded);
   * ```
   *
   * @category Utilities
   */
  bufferToBase64Url(buffer: ArrayBuffer): string {
    const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
}
